<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
        @click.self="close"
      >
        <div class="fixed inset-0 bg-dark-950/80 backdrop-blur-sm" />
        
        <div class="relative w-full max-w-xl bg-dark-800 rounded-xl shadow-2xl border border-dark-700 overflow-hidden animate-in">
          <!-- Search Input -->
          <div class="flex items-center gap-3 px-4 py-3 border-b border-dark-700">
            <MagnifyingGlassIcon class="w-5 h-5 text-dark-400" />
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              placeholder="Buscar zonas, registros, comandos..."
              class="flex-1 bg-transparent text-dark-100 placeholder-dark-500 focus:outline-none text-lg"
              @keydown.down.prevent="selectNext"
              @keydown.up.prevent="selectPrev"
              @keydown.enter.prevent="executeSelected"
              @keydown.escape="close"
            />
            <kbd class="px-2 py-0.5 bg-dark-700 text-dark-400 text-xs rounded">ESC</kbd>
          </div>

          <!-- Results -->
          <div class="max-h-80 overflow-y-auto">
            <!-- Quick Actions -->
            <div v-if="!query" class="p-2">
              <p class="px-3 py-2 text-xs font-medium text-dark-500 uppercase tracking-wider">
                Ações Rápidas
              </p>
              <button
                v-for="(action, index) in quickActions"
                :key="action.id"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                :class="selectedIndex === index ? 'bg-primary-600/20 text-primary-400' : 'hover:bg-dark-700 text-dark-200'"
                @click="executeAction(action)"
                @mouseenter="selectedIndex = index"
              >
                <component :is="action.icon" class="w-5 h-5" />
                <span>{{ action.label }}</span>
                <kbd v-if="action.shortcut" class="ml-auto px-2 py-0.5 bg-dark-700 text-dark-400 text-xs rounded">
                  {{ action.shortcut }}
                </kbd>
              </button>
            </div>

            <!-- Search Results -->
            <div v-else class="p-2">
              <template v-if="loading">
                <div class="flex items-center justify-center py-8">
                  <div class="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                </div>
              </template>
              
              <template v-else-if="results.length > 0">
                <p class="px-3 py-2 text-xs font-medium text-dark-500 uppercase tracking-wider">
                  Resultados ({{ results.length }})
                </p>
                <button
                  v-for="(result, index) in results"
                  :key="result.id"
                  class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                  :class="selectedIndex === index ? 'bg-primary-600/20 text-primary-400' : 'hover:bg-dark-700 text-dark-200'"
                  @click="navigateTo(result)"
                  @mouseenter="selectedIndex = index"
                >
                  <component :is="getResultIcon(result.type)" class="w-5 h-5 text-dark-400" />
                  <div class="flex-1 text-left">
                    <p class="text-sm">{{ result.title }}</p>
                    <p class="text-xs text-dark-500">{{ result.subtitle }}</p>
                  </div>
                  <span class="badge badge-neutral">{{ result.type }}</span>
                </button>
              </template>

              <template v-else>
                <div class="flex flex-col items-center justify-center py-8 text-dark-500">
                  <MagnifyingGlassIcon class="w-8 h-8 mb-2" />
                  <p>Nenhum resultado encontrado</p>
                </div>
              </template>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-4 py-2 border-t border-dark-700 flex items-center gap-4 text-xs text-dark-500">
            <span class="flex items-center gap-1">
              <ArrowUpIcon class="w-3 h-3" />
              <ArrowDownIcon class="w-3 h-3" />
              navegar
            </span>
            <span class="flex items-center gap-1">
              <span class="px-1 py-0.5 bg-dark-700 rounded text-[10px]">↵</span>
              selecionar
            </span>
            <span class="flex items-center gap-1">
              <span class="px-1 py-0.5 bg-dark-700 rounded text-[10px]">esc</span>
              fechar
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { api } from '@/services/api'
import {
  MagnifyingGlassIcon,
  PlusIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ServerStackIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  Cog6ToothIcon,
} from '@heroicons/vue/24/outline'

const router = useRouter()

const isOpen = ref(false)
const query = ref('')
const loading = ref(false)
const selectedIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

interface SearchResult {
  id: string
  type: 'zone' | 'record' | 'server' | 'action'
  title: string
  subtitle: string
  path?: string
}

const results = ref<SearchResult[]>([])

const quickActions = [
  { id: 'new-zone', label: 'Criar Nova Zona', icon: PlusIcon, shortcut: '⌘N', action: () => router.push('/zones?new=1') },
  { id: 'dashboard', label: 'Ir para Dashboard', icon: ChartBarIcon, action: () => router.push('/') },
  { id: 'zones', label: 'Ver Zonas DNS', icon: GlobeAltIcon, action: () => router.push('/zones') },
  { id: 'servers', label: 'Ver Servidores', icon: ServerStackIcon, action: () => router.push('/servers') },
  { id: 'settings', label: 'Configurações', icon: Cog6ToothIcon, action: () => router.push('/settings') },
]

function getResultIcon(type: string) {
  switch (type) {
    case 'zone': return GlobeAltIcon
    case 'record': return DocumentTextIcon
    case 'server': return ServerStackIcon
    default: return MagnifyingGlassIcon
  }
}

const search = useDebounceFn(async (q: string) => {
  if (!q.trim()) {
    results.value = []
    return
  }

  loading.value = true
  try {
    const response = await api.get('/api/v1/search', { params: { q } })
    results.value = response.data
    selectedIndex.value = 0
  } catch {
    results.value = []
  } finally {
    loading.value = false
  }
}, 300)

watch(query, (val) => {
  if (val) {
    search(val)
  } else {
    results.value = []
    selectedIndex.value = 0
  }
})

function open() {
  isOpen.value = true
  query.value = ''
  selectedIndex.value = 0
  nextTick(() => inputRef.value?.focus())
}

function close() {
  isOpen.value = false
  query.value = ''
}

function selectNext() {
  const max = query.value ? results.value.length : quickActions.length
  selectedIndex.value = (selectedIndex.value + 1) % max
}

function selectPrev() {
  const max = query.value ? results.value.length : quickActions.length
  selectedIndex.value = (selectedIndex.value - 1 + max) % max
}

function executeSelected() {
  if (query.value && results.value[selectedIndex.value]) {
    navigateTo(results.value[selectedIndex.value])
  } else if (!query.value && quickActions[selectedIndex.value]) {
    executeAction(quickActions[selectedIndex.value])
  }
}

function executeAction(action: typeof quickActions[0]) {
  action.action()
  close()
}

function navigateTo(result: SearchResult) {
  if (result.path) {
    router.push(result.path)
  }
  close()
}

// Keyboard shortcut
function handleKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    if (isOpen.value) {
      close()
    } else {
      open()
    }
  }
}

// Custom event listener
function handleOpenEvent() {
  open()
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('open-command-palette', handleOpenEvent)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('open-command-palette', handleOpenEvent)
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from > div:last-child,
.modal-leave-to > div:last-child {
  transform: scale(0.95) translateY(-20px);
}
</style>
